import { defineNuxtPlugin } from 'nuxt/app'
import { shallowReactive, shallowRef } from 'vue'
import type { Router } from 'vue-router'
import { isTrackingParam } from '../types/tracking-params'

// This plugin tricks Vue Router into thinking tracking params don't exist
// to prevent SSR hydration mismatches, while the 00-preserve-tracking plugin
// ensures they remain in the actual browser URL for analytics
export default defineNuxtPlugin({
  name: 'strip-tracking-params',
  order: -100,
  enforce: 'pre',
  async setup(nuxtApp) {
    // Function to strip tracking params from Vue Router's internal state
    const stripParams = () => {
      try {
        const router = nuxtApp.$router as Router
        if (!router) return false

        const currentRoute = router.currentRoute.value
        if (
          !currentRoute ||
          !currentRoute.query ||
          typeof currentRoute.query !== 'object'
        ) {
          return true
        }

        const query = { ...currentRoute.query }
        let paramsStripped = false
        const strippedParams: string[] = []

        // Remove tracking params from the query (supports regex patterns)
        for (const param of Object.keys(query)) {
          if (isTrackingParam(param)) {
            strippedParams.push(param)
            delete query[param]
            paramsStripped = true
          }
        }

        if (paramsStripped) {
          // Create a new route record without the tracking params
          let resolvedFullPath: string
          try {
            const resolved = router.resolve({
              path: currentRoute.path,
              query,
              hash: currentRoute.hash,
            })
            resolvedFullPath = resolved.fullPath
          } catch (e) {
            // Fallback: build fullPath manually
            const queryString = new URLSearchParams(query as any).toString()
            resolvedFullPath =
              currentRoute.path +
              (queryString ? '?' + queryString : '') +
              (currentRoute.hash || '')
          }

          const cleanRoute = {
            ...currentRoute,
            query,
            fullPath: resolvedFullPath,
          }

          // Update the current route's query without navigation
          // @ts-ignore - we know this internal API exists
          router.currentRoute.value = cleanRoute

          // Also update history state to match
          try {
            if (router?.options?.history?.state) {
              router.options.history.state.query = query
            }
          } catch (e) {
            // Ignore history state errors
          }

          try {
            const _route = shallowRef(router.currentRoute.value)
            const route = {}
            for (const key in _route.value) {
              Object.defineProperty(route, key, {
                get: () => _route.value[key],
                enumerable: true,
              })
            }
            // @ts-ignore - we know this internal API exists
            nuxtApp._route = shallowReactive(route)
          } catch (e) {
            // Ignore route update errors
          }

          // Log once that we've cleaned Vue Router's state
          console.log(
            '[strip-tracking] Removed',
            strippedParams.length,
            'tracking params from Vue Router state',
          )
        }

        return true
      } catch (error) {
        // Don't throw - better to have tracking params than a broken site
        console.error('[strip-tracking] Error:', error)
        return false
      }
    }

    // Try to strip params immediately
    const immediate = stripParams()

    // If router wasn't ready, try on app:created
    if (!immediate) {
      nuxtApp.hook('app:created', () => {
        stripParams()
      })
    }

    // Also try on app:beforeMount as extra safety
    nuxtApp.hook('app:beforeMount', () => {
      stripParams()
    })
  },
})
